import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { usersAPI } from '../services/api';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialog, setDialog] = useState({ open: false, user: null });
  const [form, setForm] = useState({
    email: '',
    name: '',
    role: 'support',
    is_active: true,
    can_approve_patches: false,
    can_review_backups: false,
    can_edit_kb: false,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.list();
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setForm({
        email: user.email,
        name: user.name || '',
        role: user.role,
        is_active: user.is_active,
        can_approve_patches: !!user.can_approve_patches,
        can_review_backups: !!user.can_review_backups,
        can_edit_kb: !!user.can_edit_kb,
      });
    } else {
      setForm({
        email: '',
        name: '',
        role: 'support',
        is_active: true,
        can_approve_patches: false,
        can_review_backups: false,
        can_edit_kb: false,
      });
    }
    setDialog({ open: true, user });
  };

  const handleSave = async () => {
    try {
      if (dialog.user) {
        await usersAPI.update(dialog.user.id, form);
        setSuccess('User updated successfully');
      } else {
        await usersAPI.create(form);
        setSuccess('User created successfully');
      }
      setDialog({ open: false, user: null });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user? They will lose access to the system.')) return;

    try {
      await usersAPI.delete(userId);
      setSuccess('User deleted');
      loadUsers();
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      support: 'Support (Read-only)',
      cloud_services: 'Cloud Services (Admin)',
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    return role === 'cloud_services' ? 'error' : 'info';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Patch Approver</TableCell>
              <TableCell>Report Reviewer</TableCell>
              <TableCell>KB Editor</TableCell>
              <TableCell>Last login</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.name || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={getRoleLabel(user.role)}
                    color={getRoleColor(user.role)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.is_active ? 'Active' : 'Inactive'}
                    color={user.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.can_approve_patches ? (
                    <Chip label="Approver" color="warning" size="small" />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {user.can_review_backups ? (
                    <Chip label="Reviewer" color="secondary" size="small" />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {user.can_edit_kb ? <Chip label="Editor" color="info" size="small" /> : '-'}
                </TableCell>
                <TableCell>
                  {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never recorded'}
                </TableCell>
                <TableCell>
                  {new Date(user.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpenDialog(user)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(user.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* User Dialog */}
      <Dialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialog.user ? 'Edit User' : 'Add User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                disabled={!!dialog.user}
                helperText={dialog.user ? "Email cannot be changed" : "User's email address"}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label="Role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                helperText="Support = Read-only access, Cloud Services = Full admin access"
              >
                <MenuItem value="support">Support (Read-only)</MenuItem>
                <MenuItem value="cloud_services">Cloud Services (Admin)</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.can_approve_patches}
                    onChange={(e) => setForm({ ...form, can_approve_patches: e.target.checked })}
                  />
                }
                label="Can approve patches"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.can_review_backups}
                    onChange={(e) => setForm({ ...form, can_review_backups: e.target.checked })}
                  />
                }
                label="Can review and approve backup and failed login reports"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.can_edit_kb}
                    onChange={(e) => setForm({ ...form, can_edit_kb: e.target.checked })}
                  />
                }
                label="Can edit the knowledge base"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!form.email || !form.role}
          >
            {dialog.user ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;
